import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, History, Info } from 'lucide-react';
import { Table, Badge, EmptyState } from '../../ui-v2.5';
import { getHistorialPersona } from '../api';
import {
  ESTADO_CITA, ESTADO_PARTICIPACION, ESTADO_CAMPANA, MODALIDAD, etiquetaDe, detalleCancelacion,
} from '../compartidos/etiquetasEstado';

/**
 * Pestaña «Citas técnicas» de la ficha de persona: sus campañas, sus citas y quién la añadió,
 * movió o quitó. De SOLO LECTURA (está en TABS_SOLO_LECTURA de la ficha).
 *
 * La ficha la enseña sólo con `intervenciones.campanas.view`, que es también la guardia del
 * endpoint: aquí sólo se decide qué se pinta, la barrera está en el servidor.
 *
 * El ámbito sale de `tipo` (la ficha de una colaboradora o la de una interna): los ids de las dos
 * tablas se solapan, y sin el ámbito el servidor no sabría de quién se habla.
 *
 * Todo lo escrito por personas —título de campaña, nombre de lote, nombres— se pinta como TEXTO.
 */

const fechaCorta = (yyyyMmDd) => {
  if (!yyyyMmDd) return '—';
  const [y, m, d] = String(yyyyMmDd).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};
const fechaHora = (iso) => {
  if (!iso) return '—';
  const f = new Date(iso);
  if (Number.isNaN(f.getTime())) return String(iso);
  return f.toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const nombreLote = (l) => {
  if (!l) return null;
  return l.nombre ? `Lote ${l.numero} · ${l.nombre}` : `Lote ${l.numero}`;
};

function textoEvento(e) {
  const de = e.lote_origen ? ` del lote ${e.lote_origen.numero}` : '';
  const a = e.lote_destino ? ` al lote ${e.lote_destino.numero}` : '';
  const desdeAsistente = e.origen === 'asistente' ? ' (desde el asistente de la campaña)' : '';
  if (e.accion === 'participante_anadido') return `Añadida${a}${desdeAsistente}`;
  if (e.accion === 'participante_quitado') {
    return `Quitada${de}${desdeAsistente}${e.cita_cancelada ? ' · se canceló su cita' : ''}`;
  }
  if (e.accion === 'participante_movido') return `Movida${de}${a}`;
  // Intervenciones sin cita (24/10).
  const modo = e.modalidad ? etiquetaDe(MODALIDAD, e.modalidad).label.toLowerCase() : null;
  if (e.accion === 'intervencion_registrada') return `Intervención registrada${modo ? ` (${modo})` : ''}`;
  if (e.accion === 'intervencion_deshecha') return `Intervención deshecha${modo ? ` (${modo})` : ''}: vuelve a como estaba`;
  return e.accion;
}

const COLUMNAS_CITAS = [
  { key: 'fecha', header: 'Fecha', render: (c) => fechaCorta(c.fecha) },
  { key: 'hora', header: 'Hora', render: (c) => (c.hora_inicio ? `${c.hora_inicio}–${c.hora_fin || ''}` : '—') },
  {
    key: 'estado', header: 'Estado',
    render: (c) => { const m = etiquetaDe(ESTADO_CITA, c.estado); return <Badge tone={m.tone}>{m.label}</Badge>; },
  },
  {
    // Cómo se hizo: con cita, o registrada sin cita (presencial, teléfono, remoto, Teams; sin hora).
    key: 'modalidad', header: 'Modalidad',
    render: (c) => { const m = etiquetaDe(MODALIDAD, c.modalidad || 'con_cita'); return <Badge tone={m.tone}>{m.label}</Badge>; },
  },
  { key: 'tecnico', header: 'Técnico', render: (c) => (c.tecnico ? (c.tecnico.nombre || `Persona #${c.tecnico.id}`) : 'Sin asignar') },
  { key: 'realizada', header: 'Realizada', render: (c) => (c.fecha_realizacion ? fechaHora(c.fecha_realizacion) : '—') },
  {
    // 3/11 · quién la canceló (o la marcó «no asistió») y el motivo; en una realizada, el motivo de
    // cerrarla con subtareas sin hacer. Texto plano.
    key: 'detalle', header: 'Detalle',
    render: (c) => detalleCancelacion(c) || (c.estado === 'realizada' && c.motivo ? `Motivo del cierre: ${c.motivo}` : '—'),
  },
];

function TarjetaCampana({ entrada }) {
  const { campana, lote, participacion, ya_no_esta: yaNoEsta, citas, eventos } = entrada;
  const estadoCampana = etiquetaDe(ESTADO_CAMPANA, campana.estado);
  const estadoParticipacion = participacion ? etiquetaDe(ESTADO_PARTICIPACION, participacion.estado) : null;

  return (
    <section className="rounded-v25-lg border border-v25-line bg-white p-4">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="mr-1 text-sm font-semibold text-v25-ink-900">{campana.titulo || `Campaña #${campana.id}`}</h3>
        <Badge tone={estadoCampana.tone}>{estadoCampana.label}</Badge>
        {yaNoEsta ? (
          <Badge tone="expired">Ya no está en la campaña</Badge>
        ) : (
          <>
            {lote && <Badge tone="info">{nombreLote(lote)}</Badge>}
            {estadoParticipacion && <Badge tone={estadoParticipacion.tone}>{estadoParticipacion.label}</Badge>}
          </>
        )}
      </header>
      {participacion && (
        <p className="mb-3 text-xs text-v25-ink-500">En la campaña desde {fechaHora(participacion.desde)}</p>
      )}

      <Table
        columns={COLUMNAS_CITAS}
        rows={citas}
        emptyState={<EmptyState variant="minimal" title="Sin citas en esta campaña." />}
      />

      <div className="mt-3">
        <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-v25-ink-500">
          <History className="h-3 w-3" /> Movimientos
        </p>
        {eventos.length === 0 ? (
          <p className="text-xs text-v25-ink-500">Sin movimientos registrados.</p>
        ) : (
          <ul className="space-y-1">
            {eventos.map((e) => (
              <li key={e.id} className="text-xs text-v25-ink-700">
                <span className="text-v25-ink-500">{fechaHora(e.fecha)}</span>
                {' · '}{textoEvento(e)}
                {' · '}
                {e.realizado_por
                  ? <>por {e.realizado_por.nombre || `Persona #${e.realizado_por.id}`}</>
                  : <span className="text-v25-ink-500">sin autor registrado</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export function PestanaCitasTecnicas({ personaId, tipo }) {
  const ambito = tipo === 'colaboradora' ? 'colaborador' : 'interno';
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      setDatos(await getHistorialPersona(ambito, personaId));
    } catch (e) {
      setError(e?.response?.data?.error || 'No se ha podido cargar el historial de citas técnicas.');
    } finally {
      setCargando(false);
    }
  }, [ambito, personaId]);

  useEffect(() => { if (personaId) cargar(); }, [personaId, cargar]);

  return (
    <div className="space-y-3">
      <p className="flex items-start gap-2 rounded-v25-md border border-v25-info-bd bg-v25-info-bg px-3 py-2 text-xs text-v25-info-fg">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Para cambios anteriores a noviembre de 2026 puede no constar quién los hizo, y quien se quitó
          sin cita puede no aparecer.
        </span>
      </p>

      {cargando && <p className="text-sm text-v25-ink-500">Cargando…</p>}
      {!cargando && error && (
        <div className="rounded-v25-lg border border-v25-line bg-white">
          <EmptyState variant="medium" title={error}
            action={<button type="button" onClick={cargar} className="text-sm text-v25-brand-800 underline">Reintentar</button>} />
        </div>
      )}
      {!cargando && !error && datos && datos.campanas.length === 0 && (
        <div className="rounded-v25-lg border border-v25-line bg-white">
          <EmptyState icon={CalendarClock} title="Sin citas técnicas"
            description="Esta persona no ha estado en ninguna campaña de citas técnicas, o se la quitó antes de que constara." />
        </div>
      )}
      {!cargando && !error && datos && datos.campanas.map((entrada) => (
        <TarjetaCampana key={entrada.campana.id} entrada={entrada} />
      ))}
    </div>
  );
}

export default PestanaCitasTecnicas;
